// Plan dates arrive as ISO calendar dates ("2026-10-15"). They are parsed
// as local calendar days, never through Date("2026-10-15"), which is UTC
// midnight and shows the previous day west of Greenwich. Formatting is
// en-GB on purpose: the product and its key dates are UK admissions.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parsePlanDate(value) {
  if (typeof value !== 'string') return null;
  const match = ISO_DATE.exec(value);
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date;
}

export function formatPlanDate(value) {
  const date = parsePlanDate(value);
  if (!date) return null;
  return `${date.getDate()} ${MONTHS[date.getMonth()]}`;
}

export function formatLongPlanDate(value) {
  const date = parsePlanDate(value);
  if (!date) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

// "By 4 Oct", "12–16 Oct", "30 Dec – 2 Jan", or null when the item has no
// usable date.
export function itemTiming(item) {
  const single = formatPlanDate(item?.target_date);
  if (single) return single;
  const start = parsePlanDate(item?.window_start);
  const end = parsePlanDate(item?.window_end);
  if (!start || !end) return null;
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${start.getDate()}–${end.getDate()} ${MONTHS[start.getMonth()]}`;
  }
  return `${formatPlanDate(item.window_start)} – ${formatPlanDate(item.window_end)}`;
}

// The event date tile: a month band over a day, or a day range for a window.
export function dateTileParts(item) {
  const single = parsePlanDate(item?.target_date);
  if (single) return { month: MONTHS[single.getMonth()], day: String(single.getDate()), isWindow: false };
  const start = parsePlanDate(item?.window_start);
  const end = parsePlanDate(item?.window_end);
  if (!start || !end) return null;
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  return {
    month: sameMonth ? MONTHS[start.getMonth()] : `${MONTHS[start.getMonth()]}–${MONTHS[end.getMonth()]}`,
    day: `${start.getDate()}–${end.getDate()}`,
    isWindow: true,
  };
}

// An official date as a sentence fragment for the "date has changed" notice:
// "16 October 2026", or "12–16 October 2026" for a window.
export function describeOfficialDate(entry) {
  if (!entry) return null;
  if (entry.date) return formatLongPlanDate(entry.date);
  const start = parsePlanDate(entry.window_start);
  const end = parsePlanDate(entry.window_end);
  if (!start || !end) return null;
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${start.getDate()}–${formatLongPlanDate(entry.window_end)}`;
  }
  return `${formatLongPlanDate(entry.window_start)} – ${formatLongPlanDate(entry.window_end)}`;
}

export function isOverdue(item, now = new Date()) {
  if ((item?.progress_status || 'pending') !== 'pending') return false;
  const due = parsePlanDate(item.target_date) || parsePlanDate(item.window_end);
  if (!due) return false;
  const endOfDay = new Date(due.getFullYear(), due.getMonth(), due.getDate(), 23, 59, 59);
  return endOfDay < now;
}
